// MessageAttachmentItem.jsx

import React, { useMemo } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { getNodeTypeLabel } from 'utilities/NodeTypeLabels';

// CSS
import './MessageAttachmentItem.css';

// Components
import ListViewItem from 'views/list/item/ListViewItem';
import { buildListViewItemData, resolveListViewItemConfig } from 'views/list/item/listViewItemData';
import PhotoCard from 'views/gallery/cards/PhotoCard';
import VideoCard from 'views/gallery/cards/VideoCard';

/**
 * MessageAttachmentNodeListItem
 *
 * Renders a hydrated node_ref attachment as a default-field list item.
 */
const MessageAttachmentNodeListItem = React.memo(({ node, onOpenAttachmentNode }) => {
  const language = useCurrentLanguage();
  const { getType } = useNodeTypes();

  const typeKey = String(node?.type || 'NODE').trim() || 'NODE';
  const typeDef = getType(typeKey) || null;
  const typeLabel = getNodeTypeLabel(getType, typeKey, { fallback: 'Node' });

  const listItemConfig = useMemo(() => (
    resolveListViewItemConfig({
      forceDefaultFields: true,
      typeDef,
    })
  ), [typeDef]);

  const itemData = useMemo(() => (
    buildListViewItemData({
      config: listItemConfig,
      language,
      node,
    })
  ), [language, listItemConfig, node]);

  const fieldValues = useMemo(() => {
    const next = [];

    if (typeLabel) {
      next.push({
        kind: 'text',
        label: 'Type',
        value: typeLabel,
      });
    }

    if (Array.isArray(itemData.fieldValues) && itemData.fieldValues.length) {
      next.push(...itemData.fieldValues);
    }

    return next;
  }, [itemData.fieldValues, typeLabel]);

  const openNode = () => onOpenAttachmentNode?.(node);

  return (
    <div className="message-attachment-list-item-wrap">
      <ListViewItem
        node={node}
        coverUrl={itemData.coverUrl}
        subtitle={itemData.subtitle}
        enumPills={itemData.enumPills}
        fieldValues={fieldValues}
        onClick={openNode}
        onDoubleClick={openNode}
      />
    </div>
  );
});

MessageAttachmentNodeListItem.displayName = 'MessageAttachmentNodeListItem';

/**
 * MessageAttachmentItem
 *
 * @returns {ReactNode} The rendered component.
 */
const MessageAttachmentItem = React.memo(({ model, onOpenAttachmentNode }) => {
  if (model.kind === 'image') {
    return (
      <img
        className="message-attachment-image"
        src={model.url}
        alt={model.alt}
      />
    );
  }

  if (model.kind === 'unavailable') {
    return (
      <div
        className="message-attachment-node is-unavailable"
      >

        {/* MESSAGE ATTACHMENT NODE COPY */}
        <div className="message-attachment-node-copy">

          {/* MESSAGE ATTACHMENT NODE TITLE */}
          <div
            className="message-attachment-node-title"
          >
            Item unavailable
          </div>

          {/* MESSAGE ATTACHMENT NODE TYPE */}
          <div
            className="message-attachment-node-type"
          >
            Unavailable
          </div>
        </div>
      </div>
    );
  }

  if (model.kind === 'loading') {
    return (
      <div
        className="message-attachment-node"
      >

        {/* MESSAGE ATTACHMENT NODE COPY */}
        <div className="message-attachment-node-copy">

          {/* MESSAGE ATTACHMENT NODE TITLE */}
          <div
            className="message-attachment-node-title"
          >
            {model.title}
          </div>

          {/* MESSAGE ATTACHMENT NODE TYPE */}
          <div
            className="message-attachment-node-type"
          >
            Syncing media...
          </div>
        </div>
      </div>
    );
  }

  if (model.kind === 'photo') {
    return (
      <button
        className="message-attachment-media-card"
        type="button"
        onClick={() => onOpenAttachmentNode?.(model.node)}
      >
        <PhotoCard node={model.node} reserveAspectRatio={false} />
      </button>
    );
  }

  if (model.kind === 'video') {
    return (
      <button
        className="message-attachment-media-card"
        type="button"
        onClick={() => onOpenAttachmentNode?.(model.node)}
      >
        <VideoCard node={model.node} reserveAspectRatio={false} />
      </button>
    );
  }

  // ----- RENDER ----- //

  return (
    <MessageAttachmentNodeListItem
      node={model.node}
      onOpenAttachmentNode={onOpenAttachmentNode}
    />
  );
});

// Dev Tools
MessageAttachmentItem.displayName = 'MessageAttachmentItem';

export default MessageAttachmentItem;
